import React from 'react';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Box } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Make it stand out';
export const stepDescription =
  'Add photos, amenities, and a great description to attract tenants.';

const MakeItStandOutIntro: React.FC = () => (
  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        minHeight: 500,
        width: '100%',
        maxWidth: 1100,
        gap: { xs: 4, md: 8 },
        padding: { xs: '16px 0', md: '32px 0' },
        textAlign: { xs: 'center', md: 'left' },
      }}
    >
      {/* Left: Text content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: { xs: '100%', md: '50%' },
          order: { xs: 2, md: 1 }, // Text appears below image on mobile
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}
        >
          Step 2
        </Typography>
        <Typography
          variant='h3'
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            lineHeight: 1.1,
            mb: 2,
          }}
        >
          Make it stand out
        </Typography>
        <Typography
          variant='body1'
          sx={{ color: 'text.secondary', fontSize: '1.125rem', maxWidth: 500 }}
        >
          Bring your listing to life with photos, a list of amenities, and a
          standout description.
        </Typography>
      </Box>

      {/* Right: Illustration */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: { xs: '80%', sm: '60%', md: '50%' },
          order: { xs: 1, md: 2 }, // Image appears above text on mobile
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 350,
            height: 280,
            backgroundColor: '#f8f9fa',
            borderRadius: 4,
            border: '2px solid #e9ecef',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 40% 70%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)
              `,
            }}
          />

          {/* Camera icon */}
          <PhotoCameraIcon
            sx={{
              fontSize: 80,
              color: '#6b7280',
              mb: 2,
              zIndex: 1,
            }}
          />

          {/* Mock photo frames */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              zIndex: 1,
            }}
          >
            {[1, 2, 3].map(index => (
              <Box
                key={index}
                sx={{
                  width: 60,
                  height: 40,
                  backgroundColor: 'white',
                  borderRadius: 1,
                  border: '1px solid #d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
              </Box>
            ))}
          </Box>

          {/* Decorative dots */}
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#f59e0b',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 30,
              left: 30,
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#10b981',
            }}
          />
        </Box>
      </Box>
    </Box>
  </Box>
);

export default MakeItStandOutIntro;
